import type { Attachment } from 'sr-types/lib/production/v1/graphql';
import { SECURE_ATTACHMENT_DOWNLOAD_ROUTE_PATH } from '../constants/constants';
import {
    MATERIAL_REFERENCE_ATTRIBUTE,
    MATERIAL_REFERENCE_CLASS,
    MATERIAL_REFERENCE_ID_ATTRIBUTE,
    MATERIAL_REFERENCE_LABEL_ATTRIBUTE,
    MATERIAL_REFERENCE_LABEL_CLASS,
    MATERIAL_REFERENCE_MIME_TYPE_ATTRIBUTE,
    MATERIAL_REFERENCE_TYPE_ATTRIBUTE,
    SPACE_KEYS
} from './lexicalConstants';

export type AttachmentReferenceType = 'attachment' | 'link';

export type AttachmentReferenceData = {
    referenceId: string;
    referenceType: AttachmentReferenceType;
    label: string;
    mimeType?: string;
};

const ATTACHMENT_REFERENCE_ID_PATTERN = /^attachment_id_(\d+)$/;

// Generate a reference type attribute based on the reference type and ID.
export const getReferenceTypeAttribute = (referenceType: AttachmentReferenceType, referenceId: string): string => {
    // Check if the referenceId follows the pattern for attachment_id_${index}.
    const match = referenceId.match(ATTACHMENT_REFERENCE_ID_PATTERN);
    const matchedAttachmentId = match?.[1];
    return matchedAttachmentId ? `attachment_type_${matchedAttachmentId}` : referenceType;
};

export const getAttachmentReferenceType = (attachment: Attachment): AttachmentReferenceType =>
    attachment.isLink ? 'link' : 'attachment';

export const isSpaceKey = (key: string) => SPACE_KEYS.has(key);

export const buildAttachmentDownloadUrl = (referenceId: string, baseUri?: string): string => {
    const normalizedBaseUri = baseUri ? baseUri.replace(/\/$/, '') : '';
    const downloadPath = SECURE_ATTACHMENT_DOWNLOAD_ROUTE_PATH.replace(':assetId', encodeURIComponent(referenceId));
    return normalizedBaseUri ? `${normalizedBaseUri}${downloadPath}` : downloadPath;
};

export const createAttachmentReferenceElement = ({
    referenceId,
    referenceType,
    label,
    mimeType
}: AttachmentReferenceData) => {
    const baseUri = (window as Window & { _env_?: { BASE_URI?: string } })._env_?.BASE_URI;
    const referenceTypeAttribute = getReferenceTypeAttribute(referenceType, referenceId);

    const element = document.createElement('a');
    element.className = MATERIAL_REFERENCE_CLASS;
    element.setAttribute('href', buildAttachmentDownloadUrl(referenceId, baseUri));
    element.setAttribute('target', '_blank');
    element.setAttribute('rel', 'noopener noreferrer');
    element.setAttribute(MATERIAL_REFERENCE_ATTRIBUTE, 'true');
    element.setAttribute(MATERIAL_REFERENCE_ID_ATTRIBUTE, referenceId);
    element.setAttribute(MATERIAL_REFERENCE_TYPE_ATTRIBUTE, referenceTypeAttribute);
    element.setAttribute(MATERIAL_REFERENCE_LABEL_ATTRIBUTE, label);
    if (mimeType) {
        element.setAttribute(MATERIAL_REFERENCE_MIME_TYPE_ATTRIBUTE, mimeType);
    }

    const labelElement = document.createElement('span');
    labelElement.className = MATERIAL_REFERENCE_LABEL_CLASS;
    labelElement.textContent = label;
    element.append(labelElement);

    const attachmentReferenceElement: HTMLAnchorElement = element;
    return attachmentReferenceElement;
};
